import { useT } from "../i18n/index.js";
import { Button } from "../ui/Button.js";
import { useUnits } from "./state.js";
import { useSession } from "../state/session.js";
import { AsyncToggle } from "../ui/AsyncToggle.js";
import { autoEquipTargets } from "./unitSheetModel.js";
import type { PullView } from "./types.js";

/** After a draw: dress the new units with the best free modules once, or switch it on for good (D69). */
export function RevealAutoEquip({ pulls }: { pulls: PullView[] }) {
  const t = useT();
  const roster = useUnits((s) => s.roster);
  const autoEquip = useUnits((s) => s.autoEquip);
  const settings = useSession((s) => s.settings);
  const updateSettings = useSession((s) => s.updateSettings);
  const done = useUnits((s) => s.revealDressed ?? null);
  const setDone = useUnits((s) => s.setRevealDressed);
  const dressing = useUnits((s) => s.revealDressing ?? false);
  const setDressing = useUnits((s) => s.setRevealDressing);
  const fresh = new Set(pulls.filter((p) => p.isNew).map((p) => p.unitId));
  const targets = roster ? autoEquipTargets(roster.units.filter((u) => fresh.has(u.unitId)), roster.modules) : [];
  // New units drawn: offer the one-time dressing when something fits, and the always-on switch either way.
  if (fresh.size === 0) return null;
  const dress = async () => {
    setDressing(true);
    let equipped = 0;
    try {
      for (const id of targets) equipped += await autoEquip(id);
      setDone(equipped);
    } finally {
      setDressing(false);
    }
  };
  return (
    <div className="row reveal-auto-equip" style={{ marginTop: 10, gap: 8, flexWrap: "wrap" }} data-testid="reveal-auto-equip">
      {done !== null ? (
        // The button stays after dressing so the summary tells what happened instead of silently losing a control.
        <Button variant="gold" disabled data-testid="reveal-auto-equip-now" data-state="done">
          {done > 0 ? t("units.autoEquip.done", { n: done }) : t("units.autoEquip.none")}
        </Button>
      ) : dressing ? (
        // One request per unit: the button stays put and busy until the last one answered.
        <Button variant="gold" loading data-testid="reveal-auto-equip-now" data-state="busy">
          {t("units.autoEquip.newUnits")}
        </Button>
      ) : targets.length > 0 ? (
        <Button variant="gold" onClick={() => void dress()} data-testid="reveal-auto-equip-now" data-state="ready">
          {t("units.autoEquip.newUnits")} ({targets.length})
        </Button>
      ) : null}
      <AsyncToggle checked={settings?.autoEquipUnits ?? false} save={(autoEquipUnits) => updateSettings({ autoEquipUnits })} testId="reveal-auto-equip-always">
        {t("units.autoEquip.always")}
      </AsyncToggle>
    </div>
  );
}
